import axios from "../axios/AxiosInstance";

const WORKSPACE = "commercial/workspace";
const CHUNK = 200;
export const SYNCED_KINDS = ["clients", "needs", "quotes", "results", "tasks", "signals", "events", "imports", "recruitmentNeeds"];
// Personal settings saved per user; agency rules come from commercial/rules.
export const SETTINGS_FIELDS = [
  "workday", "calendar", "dailyGoals", "dailyCallGoal", "goalsByUser", "goalHistory", "commercialFavorites",
  "workSchedules", "workHours", "weeklyGoalPlans", "weeklyGoalAudit", "financialGoals", "financialGoalAudit",
];
const RULE_FIELDS = ["automationRules", "noteTemplates", "scoreWeights", "inactivityThresholds"];
// Screen state of this browser only.
const UI_FIELDS = ["commercialView", "prospectingSession", "pendingResultClientId", "workLocks"];
// Values recomputed on every load: orders, scores and the history built from events.
const CLIENT_COMPUTED = ["orders", "contracts", "invoices", "quotes", "history", "score", "scoreFactors", "scoreRaw", "scoreCapped", "riskReasons", "inactivityAlert", "owner", "agency", "apiClient", "kind"];

const uiKey = () => `jm-commercial-ui:${localStorage.getItem("userId")}`;
function readUiState() {
  try {
    const saved = JSON.parse(localStorage.getItem(uiKey()));
    return saved && typeof saved === "object" ? saved : {};
  } catch {
    return {};
  }
}
function writeUiState(state) {
  try {
    localStorage.setItem(uiKey(), JSON.stringify(Object.fromEntries(UI_FIELDS.map(field => [field, state[field] ?? null]))));
  } catch { /* Stockage local indisponible : l'écran repartira de l'état par défaut. */ }
}

const omit = (value, keys) => Object.fromEntries(Object.entries(value).filter(([key]) => !keys.includes(key)));
function payload(kind, record) {
  return kind === "clients" ? omit(record, CLIENT_COMPUTED) : record;
}
const fingerprint = (kind, record) => JSON.stringify(payload(kind, record));
const contactsPrint = (client) => JSON.stringify(client.contacts || []);

// Last version of each record known to be saved on the server.
let baseline = null;
function emptyBaseline() {
  return { records: Object.fromEntries(SYNCED_KINDS.map(kind => [kind, new Map()])), contacts: new Map(), settings: new Map() };
}
export function setCommercialBaseline(state) {
  baseline = emptyBaseline();
  for (const kind of SYNCED_KINDS)
    for (const record of state?.[kind] || [])
      if (record?.id != null) baseline.records[kind].set(String(record.id), fingerprint(kind, record));
  for (const client of state?.clients || []) if (client?.id != null) baseline.contacts.set(String(client.id), contactsPrint(client));
  for (const field of SETTINGS_FIELDS) baseline.settings.set(field, JSON.stringify(state?.[field] ?? null));
}

export function commercialChanges(state) {
  if (!baseline) return [];
  const changes = [];
  for (const kind of SYNCED_KINDS)
    for (const record of state?.[kind] || []) {
      if (record?.id == null) continue;
      const id = String(record.id), print = fingerprint(kind, record);
      if (baseline.records[kind].get(id) === print) continue;
      const data = { ...payload(kind, record), id };
      if (kind === "clients" && baseline.contacts.get(id) === contactsPrint(record)) delete data.contacts;
      changes.push({ kind, id, data, print, contacts: kind === "clients" ? contactsPrint(record) : null });
    }
  const settings = {};
  for (const field of SETTINGS_FIELDS) {
    const print = JSON.stringify(state?.[field] ?? null);
    if (baseline.settings.get(field) !== print && state?.[field] !== undefined) settings[field] = state[field];
  }
  if (Object.keys(settings).length) changes.push({ kind: "settings", id: "me", data: settings });
  return changes;
}

function markSaved(change) {
  if (change.kind === "settings") {
    for (const [field, value] of Object.entries(change.data)) baseline.settings.set(field, JSON.stringify(value ?? null));
    return;
  }
  baseline.records[change.kind].set(change.id, change.print);
  if (change.contacts != null) baseline.contacts.set(change.id, change.contacts);
}

export async function loadCommercialState() {
  const [{ data }, rules] = await Promise.all([
    axios.get(WORKSPACE),
    axios.get("commercial/rules").then(({ data }) => data, () => null),
  ]);
  return {
    ...(data?.settings || {}),
    ...Object.fromEntries(RULE_FIELDS.filter(field => rules?.[field] !== undefined).map(field => [field, rules[field]])),
    ...readUiState(),
    ...Object.fromEntries(SYNCED_KINDS.filter(kind => kind !== "clients").map(kind => [kind, Array.isArray(data?.[kind]) ? data[kind] : []])),
    engagements: Array.isArray(data?.engagements) ? data.engagements : [],
    clients: [],
  };
}

// Records corrected by the server (exclusions, cancelled tasks, call owner…)
// replace the local version when it was not modified in the meantime.
export function applyServerRecords(state, records) {
  if (!records || !baseline) return state;
  let next = state;
  for (const kind of SYNCED_KINDS) {
    const rows = records[kind];
    if (!Array.isArray(rows) || !rows.length || !Array.isArray(next[kind])) continue;
    const server = new Map(rows.map(row => [String(row.id), row]));
    const list = next[kind].map(row => {
      const id = String(row.id), saved = server.get(id);
      if (!saved) return row;
      server.delete(id);
      if (baseline.records[kind].get(id) !== fingerprint(kind, row)) return row;
      const merged = kind === "clients"
        ? { ...row, ...omit(saved, ["id", "client_id", "establishment_id", "name", "siret", "ownerId", "agency", "kind", "contacts"]) }
        : saved;
      baseline.records[kind].set(id, fingerprint(kind, merged));
      return merged;
    });
    if (kind !== "clients")
      for (const [id, row] of server) {
        list.push(row);
        baseline.records[kind].set(id, fingerprint(kind, row));
      }
    next = { ...next, [kind]: list };
  }
  return next;
}

let timer = null;
let pending = null;
let waiting = [];
let requestQueue = Promise.resolve();

async function send(state) {
  writeUiState(state);
  const changes = commercialChanges(state);
  const records = {}, errors = [];
  for (let index = 0; index < changes.length; index += CHUNK) {
    const chunk = changes.slice(index, index + CHUNK);
    const { data } = await axios.post(`${WORKSPACE}/changes`, {
      changes: chunk.map(({ kind, id, data }) => ({ kind, id, data })),
    });
    (data?.results || []).forEach((result, position) => {
      const change = chunk[position];
      if (!change) return;
      // A refused record is not sent again until it changes.
      markSaved(change);
      if (result.status === "error") errors.push(result.message);
    });
    for (const [kind, rows] of Object.entries(data?.records || {})) records[kind] = [...(records[kind] || []), ...rows];
  }
  return { records, errors: [...new Set(errors)] };
}

function sendPending() {
  if (!pending) return;
  const state = pending;
  const callers = waiting;
  pending = null;
  waiting = [];
  timer = null;
  // Les envois restent ordonnés : chaque envoi compare l'état à la dernière
  // version enregistrée, une réponse lente ne peut pas être écrasée.
  requestQueue = requestQueue.catch(() => {}).then(() => send(state));
  requestQueue.then(
    (result) => callers.forEach((caller) => caller.resolve(result)),
    (error) => callers.forEach((caller) => caller.reject(error)),
  );
}

// Regroupe les sauvegardes rapprochées ; toutes les promesses en attente suivent le dernier envoi.
export function saveCommercialState(state, delay = 1500) {
  pending = state;
  clearTimeout(timer);
  return new Promise((resolve, reject) => {
    waiting.push({ resolve, reject });
    if (delay <= 0) sendPending();
    else timer = setTimeout(sendPending, delay);
  });
}
